"use client";
import Script from "next/script";
import { useCallback, useEffect, useState } from "react";
import { Flex } from "@revoo/design-system/ui/Flex/Flex";
import { useTheme } from "@revoo/design-system/providers/ThemeProvider";
import { useMediaQuery } from "@chakra-ui/react";
import { usePathname } from "next/navigation";
import { CometChat } from "@cometchat-pro/chat";
import { UserDataModel } from "@/libs/auth/redux/auth.types";
import { useSelectCurrentUser } from "@/libs/auth/redux/auth.slice";
import { EnvVariablesType } from "@/libs/shared/hooks/useEnvVariables";
import {
  ErrorModel,
  UserChatModel,
} from "@/libs/shared/components/Chat/Chat.utils";

export async function useCometChatInit(env: EnvVariablesType) {
  try {
    await CometChat.init(
      env.COMETCHAT_APP_ID,
      new CometChat.AppSettingsBuilder()
        .subscribePresenceForAllUsers()
        .setRegion(env.COMETCHAT_REGION)
        .build(),
    );
    console.log("CometChat inizializzato");
  } catch (error) {
    console.error("Errore init CometChat:", error);
  }
}

export function initializeCometChat(env?: EnvVariablesType) {
  env && void useCometChatInit(env);

  return window.CometChatWidget?.init({
    appID: env?.COMETCHAT_APP_ID,
    appRegion: env?.COMETCHAT_REGION,
    authKey: env?.COMETCHAT_AUTH_KEY,
  }).then(
    () => console.log("CometChat initialized successfully"),
    (error: ErrorModel) =>
      console.error(
        "CometChat initialization failed with error:",
        error?.message,
      ),
  );
}

export const loginCometChat = (user?: UserDataModel) => {
  return window.CometChatWidget?.login({ uid: user?.comet_chat_user_id }).then(
    (userLogged: UserChatModel) =>
      console.log("Login successful:", userLogged?.name),
    (error: ErrorModel) =>
      console.error("Login failed with error:", error?.message),
  );
};

export const removeCometChatWidget = () => {
  const cometchatWidget = document.getElementById("cometchat__widget");
  if (cometchatWidget) {
    cometchatWidget.remove();
  }
};

export const launchCometChat = (
  user: UserDataModel,
  env: EnvVariablesType,
  isLargerThanMd: boolean,
) => {
  // Defensive: avoid duplicated widgets if `launch` happens more than once
  removeCometChatWidget();
  return window.CometChatWidget?.launch({
    widgetID: env?.COMETCHAT_WIDGET_ID,
    roundedCorners: isLargerThanMd ? "true" : "false",
    docked: isLargerThanMd ? "true" : "false",
    // On mobile we embed the widget into a fixed container (#cometchat),
    // so the widget should fill that container instead of using viewport math.
    height: isLargerThanMd ? "600px" : "100%",
    width: isLargerThanMd ? "800px" : "100%",
    defaultType: "user",
    target: "#cometchat",
    defaultID: user?.comet_chat_user_id,
  });
};

export const useCometChat = (env?: EnvVariablesType, user?: UserDataModel) => {
  const { device } = useTheme();
  const [isLargerThanMd]: boolean[] = useMediaQuery(device.md, { ssr: false });

  const restartCometChat = useCallback(async () => {
    await initializeCometChat(env);
    await loginCometChat(user);
    await launchCometChat(user!, env!, isLargerThanMd);
  }, [env, user, isLargerThanMd]);

  return {
    isLargerThanMd,
    restartCometChat,
  };
};

type UseCometChatParams = {
  env?: EnvVariablesType;
  user?: UserDataModel;
  enabled: boolean;
};

export const useCometChatWidget = ({
  env,
  user,
  enabled,
}: UseCometChatParams) => {
  const [isFirstRender, setIsFirstRender] = useState(true);
  const { isLargerThanMd, restartCometChat } = useCometChat(env, user);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    } else {
      removeCometChatWidget();
      if (enabled && env && user?.comet_chat_user_id) {
        void restartCometChat();
      }
    }
  }, [
    isLargerThanMd,
    enabled,
    env,
    user?.comet_chat_user_id,
    restartCometChat,
  ]);

  useEffect(() => {
    if (!enabled) {
      removeCometChatWidget();
    }
  }, [enabled]);

  return { isLargerThanMd };
};

const Chat = ({ env }: { env: EnvVariablesType }) => {
  const user = useSelectCurrentUser();
  const hasChat = usePathname()?.indexOf("chat") > -1;
  const { device } = useTheme();
  const [isLargerThanMd]: boolean[] = useMediaQuery(device.md, { ssr: false });

  const enabled = isLargerThanMd || hasChat;
  useCometChatWidget({ env, user: user!, enabled });

  return (
    <>
      <Script src="https://widget-js.cometchat.io/v3/cometchatwidget.js" />
      <Flex
        display={{ base: hasChat ? "flex" : "none", md: "flex" }}
        position={{
          base: hasChat && !isLargerThanMd ? "fixed" : "static",
          md: "static",
        }}
        top={{ base: hasChat && !isLargerThanMd ? 0 : "auto", md: "auto" }}
        left={{ base: hasChat && !isLargerThanMd ? 0 : "auto", md: "auto" }}
        right={{ base: hasChat && !isLargerThanMd ? 0 : "auto", md: "auto" }}
        // Menu mobile is 90px tall; add safe-area and a small buffer so the last rows
        // (e.g. Chats/Users) never end up under the bottom nav.
        bottom={{
          base:
            hasChat && !isLargerThanMd
              ? "calc(90px + env(safe-area-inset-bottom, 0px) + 8px)"
              : "auto",
          md: "auto",
        }}
        zIndex={{ base: hasChat && !isLargerThanMd ? 90 : "auto", md: "auto" }}
        w={{ base: hasChat && !isLargerThanMd ? "100vw" : "auto", md: "auto" }}
        h={{ base: hasChat && !isLargerThanMd ? "auto" : "auto", md: "auto" }}
      >
        <div
          id="cometchat"
          style={
            !isLargerThanMd && hasChat
              ? { width: "100%", height: "100%" }
              : !isLargerThanMd
                ? { zIndex: "1" }
                : {}
          }
        ></div>
      </Flex>
    </>
  );
};

export default Chat;
